import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { styles } from './styles';
import { SignUpProps, UserSignAddressInterface } from '../SignUpInterface';
import TextField from '@/components/textField/TextField';
import { SignUpAddressController as SignUpController } from './SignUpAddressController';
import { useGetCityByZip } from '@/data/services/useGetCityByZipService';
import Button from '@/components/button/Button';
import WarningField from '@/components/warningField/WarningField';

const SignUpAddress = ({
  changeStep,
  user,
  setUser,
  loading,
  setLoading,
  showWarning,
  setShowWarning,
  warningMessage,
  setWarningMessage,
}: SignUpProps) => {
  const { t } = useTranslation(['signInAddress', 'common']);
  const [nextScreen, setNextScreen] = useState(false);
  const [invalidZipCode, setInvalidZipCode] = useState(false);
  const { getCityByZipCode } = useGetCityByZip();
  const { isValid, controlledTextView, handleSubmit, triggerFieldUpdate } =
    SignUpController({
      user,
      setUser,
    });

  useEffect(() => {
    if (nextScreen) {
      setNextScreen(false);
      changeStep(true);
    }
  }, [user]);

  const onSubmit = (data: UserSignAddressInterface) => {
    setUser({
      ...user,
      zip: data.zip,
      address: data.address,
      complement: data.complement == null ? undefined : data.complement,
      city: data.city,
      state: data.state,
    });
    setNextScreen(true);
  };

  const autoFillByZip = (zip: string) => {
    if (zip.length < 5) return;
    setLoading(true);
    setShowWarning(false);
    getCityByZipCode(zip)
      .then(({ city, state }) => {
        setUser({
          ...user,
          zip,
          city,
          state,
        });
        triggerFieldUpdate('city', city);
        triggerFieldUpdate('state', state);
        setInvalidZipCode(false);
      })
      .catch(() => {
        setInvalidZipCode(true);
        setShowWarning(true);
        setWarningMessage(t('create_account_zip_auto_fill_error'));
      })
      .finally(() => setLoading(false));
  };

  const inputFields = (
    <Stack spacing={2}>
      {controlledTextView(
        <TextField
          sx={styles.zip}
          value={user.zip}
          title={t('create_account_zip')}
          placeholder={t('create_account_zip')}
          maxLength={5}
          disabled={loading}
          numberOnly
        />,
        'zip',
        autoFillByZip
      )}
      {controlledTextView(
        <TextField
          value={user.city}
          title={t('create_account_city')}
          placeholder={t('create_account_city')}
          maxLength={15}
          disabled={loading}
        />,
        'city'
      )}
      {controlledTextView(
        <TextField
          value={user.state}
          title={t('create_account_state')}
          placeholder={t('create_account_state')}
          maxLength={8}
          disabled={loading}
        />,
        'state'
      )}
      {controlledTextView(
        <TextField
          value={user.address}
          title={t('create_account_address')}
          placeholder={t('create_account_address')}
          maxLength={50}
        />,
        'address'
      )}
      {controlledTextView(
        <TextField
          value={user.complement}
          title={t('create_account_complement')}
          placeholder={t('create_account_complement')}
          maxLength={50}
          disabled={loading}
        />,
        'complement'
      )}
    </Stack>
  );

  return (
    <Stack
      spacing={4}
      sx={styles.container}
      component="form"
      onSubmit={handleSubmit(onSubmit)}
    >
      {inputFields}
      <WarningField
        description={warningMessage}
        isHidden={!showWarning}
        setShowWarning={setShowWarning}
        status="warning"
        rightButtonTitle={t('close')}
        rightButtonAction={() => setShowWarning(false)}
      />
      <Button
        title={t('common:next')}
        onClick={handleSubmit(onSubmit)}
        disable={!isValid || loading || invalidZipCode}
        loading={loading}
      />
    </Stack>
  );
};

export default SignUpAddress;
